import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useRequestStore, useAuthStore } from '../../stores/root_store';
import { REQUEST_STATUS } from '../../types';
import { stripHtml } from '../../components/SafeHtml';
import { StarRating } from '../../components/StarRating';
import { requestApi } from '../../api/request_api';
import { RequestListControls } from '../../components/RequestListControls';
import { useAccountSummary } from '../../hooks/useAccountSummary';
import { AccountSummaryCard, OutOfTasksBanner } from '../../components/AccountSummary';

const FILTER_OPTIONS = [{ value: 'archived', label: 'Archived' }];

const getStatusLabel = (status: number): string => {
  return status >= REQUEST_STATUS.CLOSED ? 'Closed' : 'Open';
};

const getStatusColor = (
  status: number
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
  return status >= REQUEST_STATUS.CLOSED ? 'success' : 'primary';
};

export const RequestListPage = observer(() => {
  const requestStore = useRequestStore();
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const { summary, outOfTasks } = useAccountSummary();
  // ?filter=archived so the archived list is linkable
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') === 'archived' ? 'archived' : '';
  const archived = filter === 'archived';

  useEffect(() => {
    requestStore.fetchRequests(true, archived);
  }, [requestStore, archived]);

  const handleFilterChange = (value: string) => {
    setSearchParams(value ? { filter: value } : {});
  };

  const handleRate = async (requestKey: string, rating: number) => {
    const updated = await requestApi.rateRequest(requestKey, rating);
    // Use set directly instead of receiveRequestUpdate to avoid duplicate
    // entries from uuid/datastore_key aliases in the Map
    requestStore.requests.set(requestKey, updated);
  };

  const handleLoadMore = () => {
    requestStore.fetchRequests();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">
          {archived ? 'Archived Requests' : 'Your Requests'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!authStore.isAssistant && (
            <Button
              variant="outlined"
              startIcon={<RepeatIcon />}
              onClick={() => navigate('/request/recurring')}
            >
              Recurring
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/request/new')}
          >
            New Request
          </Button>
        </Box>
      </Box>

      {summary && outOfTasks && <OutOfTasksBanner summary={summary} />}

      <Grid container spacing={3}>
        {/* Main Content - Request List */}
        <Grid size={{ xs: 12, md: 8 }}>
          <RequestListControls
            filterOptions={FILTER_OPTIONS}
            currentFilter={filter}
            onFilterChange={handleFilterChange}
            filterLabel="Show"
            allLabel="Active"
          />

          {requestStore.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {requestStore.error}
            </Alert>
          )}

          {requestStore.requestsLoading && requestStore.allRequests.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : requestStore.allRequests.length === 0 && archived ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No archived requests.
                </Typography>
              </CardContent>
            </Card>
          ) : requestStore.allRequests.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" gutterBottom>
                  You haven't created any requests yet.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/request/new')}
                  sx={{ mt: 2 }}
                >
                  Create Your First Request
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Stack spacing={2}>
                {requestStore.allRequests.map((request) => (
                  <Card key={request.key}>
                    <CardActionArea
                      onClick={() => navigate(`/request/${request.key}`)}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Typography variant="h6" component="div">
                            {request.title || 'Untitled Request'}
                          </Typography>
                          <Chip
                            label={getStatusLabel(request.status)}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </Box>
                        <Typography
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {request.content_type === 'text/html'
                            ? stripHtml(request.content)
                            : request.content}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          Created:{' '}
                          {new Date(request.date_created).toLocaleDateString()}
                        </Typography>
                        {request.status >= REQUEST_STATUS.CLOSED && (
                          <Box
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{ mt: 1 }}
                          >
                            <StarRating
                              compact
                              value={request.star_rating ?? null}
                              onRate={(rating) => handleRate(request.key, rating)}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>

              {requestStore.requestsHasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={requestStore.requestsLoading}
                  >
                    {requestStore.requestsLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>

        {/* Right Sidebar - Plan stats and Welcome Note */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {summary && <AccountSummaryCard summary={summary} />}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Welcome to the New Fancy Hands
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  We've updated, improved, and simplified the site to make it easier than ever to get help with your tasks.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a request and our assistants will get to work right away.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
});
